/**
 * RichDescriptionEditor — TipTap-based rich text editor for quote / change-order
 * line item descriptions.
 *
 * Supports: Bold, Italic, Bullet list
 *
 * Smart paste: When admin pastes content from Word / Google Docs / PDF that uses
 * tab-prefixed bullets like `\t•\tFoo`, `\t-\tBar`, or `•Foo` runs (with no
 * newlines between items — common with PDF copy/paste), the paste handler
 * normalises into a proper <ul><li>…</li></ul>.
 *
 * Backward compat: accepts plain-text or HTML as the initial `value` and emits HTML.
 */
import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, List } from "lucide-react";

/* ---------------- Smart paste helpers ---------------- */

const BULLET_RE = /(?:^|\s)[•·●◦▪-]\s*\t?/g; // bullet chars optionally followed by tab

/**
 * Detect text that came from a bullet-listy paste with tab-or-bullet separators
 * but missing newlines, and reflow it into one bullet per line.
 * Example input:
 *   "Foo: \t•\tA\t•\tB\t•\tC"
 * Becomes:
 *   "Foo:\n- A\n- B\n- C"
 */
function normaliseFlatBulletText(raw) {
  if (!raw) return raw;
  let text = raw
    .replace(/\u00a0/g, " ") // nbsp → space
    .replace(/\r\n?/g, "\n");

  // If the text already has newline-separated bullets, leave it alone
  const hasNewlineBullets = /\n\s*[•·●◦▪*\-]\s+/.test(text);
  if (hasNewlineBullets) return text;

  // If the text has multiple bullet glyphs in a single run, split them.
  // We split on:  whitespace + bullet-glyph + whitespace (incl tabs).
  // Skip leading hyphens in compound words by requiring whitespace before.
  const SPLIT_RE = /[\t ]*[•·●◦▪]+[\t ]+/g;
  if ((text.match(SPLIT_RE) || []).length >= 2) {
    // First chunk is the lead-in (may be a "Work to include:" preface), the rest are items
    const parts = text.split(SPLIT_RE).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const [lead, ...items] = parts;
      return [lead, ...items.map((it) => `- ${it}`)].join("\n");
    }
  }

  return text;
}

/** Convert our intermediate "- bullet\n- bullet" lines into HTML <ul><li>… */
function plainToHtml(text) {
  if (!text) return "";
  const lines = text.split("\n");
  const html = [];
  let inList = false;
  for (let line of lines) {
    line = line.replace(/\s+$/g, "");
    const bulletMatch = /^\s*[•·●◦▪*\-]\s+(.*)$/.exec(line);
    if (bulletMatch) {
      if (!inList) {
        html.push("<ul>");
        inList = true;
      }
      html.push(`<li>${escapeHtml(bulletMatch[1])}</li>`);
    } else if (line.trim() === "") {
      if (inList) {
        html.push("</ul>");
        inList = false;
      }
      html.push("<p></p>");
    } else {
      if (inList) {
        html.push("</ul>");
        inList = false;
      }
      html.push(`<p>${escapeHtml(line)}</p>`);
    }
  }
  if (inList) html.push("</ul>");
  return html.join("");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Heuristic: is `value` HTML, plain text with newlines, or a single line?
 * Returns an HTML string suitable for TipTap initialisation.
 */
function valueToHtml(value) {
  if (!value) return "";
  const isHtml = /<\/?(p|ul|ol|li|strong|em|b|i|br)\b/i.test(value);
  if (isHtml) return value;
  // Plain text — preserve newlines / bullet-style lines
  return plainToHtml(value);
}

/* ---------------- React component ---------------- */

const TOOLBAR_BTN =
  "h-7 w-7 inline-flex items-center justify-center rounded text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900 transition-colors";
const TOOLBAR_BTN_ACTIVE = "bg-[#00341d]/10 text-[#00341d]";

const ToolbarBtn = ({ onClick, active, ariaLabel, children, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    aria-label={ariaLabel}
    data-testid={testId}
    className={`${TOOLBAR_BTN} ${active ? TOOLBAR_BTN_ACTIVE : ""}`}
  >
    {children}
  </button>
);

export const RichDescriptionEditor = ({
  value,
  onChange,
  placeholder = "Description...",
  testId,
  rightSlot, // optional element rendered top-right of toolbar (e.g. AI Draft button)
  hint = "Bold · Italic · Bullets. Paste from Word, Docs, or PDF — bullets auto-detect.",
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Drop heading, codeBlock, blockquote, horizontalRule — not needed
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: false,
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: valueToHtml(value),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none focus:outline-none min-h-[80px] px-3 py-2 text-sm text-zinc-700 [&_p]:my-1 [&_ul]:my-1 [&_ul]:pl-5 [&_ul]:list-disc [&_li]:my-0",
        "data-testid": testId || "rich-desc-editor",
      },
      handlePaste(view, event) {
        // If the user is pasting from an HTML-rich source (Word, Docs, browser
        // selection), let TipTap's default handler do its thing — it preserves
        // proper <ul>/<li>/<strong>/<em> structure already.
        const cd = event.clipboardData;
        const html = cd?.getData("text/html") || "";
        if (html && /<(ul|ol|li|strong|em|b|i)\b/i.test(html)) {
          return false; // default handling
        }

        // Plain-text paste — apply our smart bullet flattener.
        const text = cd?.getData("text/plain") || "";
        if (!text) return false;
        const normalised = normaliseFlatBulletText(text);
        // If the text doesn't contain any bullet-y markup, let TipTap handle it
        // (so multi-line plain text still becomes paragraphs naturally).
        if (normalised === text && !/^\s*[•·●◦▪*\-]\s+/m.test(normalised)) {
          return false;
        }
        event.preventDefault();
        const generatedHtml = plainToHtml(normalised);
        // Insert via editor command so TipTap creates real list nodes
        // (this fires inside the synchronous paste flow, so use queueMicrotask
        // to avoid disturbing the current transaction).
        queueMicrotask(() => {
          editor?.chain().focus().insertContent(generatedHtml).run();
        });
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Treat the empty editor (`<p></p>`) as empty string for back-compat with old quotes
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep editor in sync if a parent updates `value` programmatically (e.g. AI draft)
  useEffect(() => {
    if (!editor) return;
    const incoming = valueToHtml(value);
    const current = editor.getHTML();
    if (incoming && incoming !== current && incoming !== "<p></p>") {
      editor.commands.setContent(incoming, { emitUpdate: false });
    } else if (!value && current && current !== "<p></p>") {
      editor.commands.setContent("", { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const idPrefix = testId || "rde";
  return (
    <div className="rounded-md border border-zinc-200 bg-white/50 focus-within:ring-2 focus-within:ring-[#00341d]/20 focus-within:border-[#00341d]">
      <div className="flex items-center justify-between gap-1 border-b border-zinc-200 px-2 py-1">
        <div className="flex items-center gap-0.5">
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            ariaLabel="Bold"
            testId={`${idPrefix}-bold`}
          >
            <Bold className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            ariaLabel="Italic"
            testId={`${idPrefix}-italic`}
          >
            <Italic className="w-3.5 h-3.5" />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            ariaLabel="Bullet list"
            testId={`${idPrefix}-bullet`}
          >
            <List className="w-3.5 h-3.5" />
          </ToolbarBtn>
        </div>
        {rightSlot}
      </div>
      <EditorContent editor={editor} />
      {hint && (
        <div className="px-3 pb-1.5 text-[10px] text-zinc-400 select-none">
          {hint}
        </div>
      )}
    </div>
  );
};

export default RichDescriptionEditor;
